/**
 * Data layer for `metrics-cpc-cpa-epc-avg-sale-cr-roi-roas-revenue-payout-profit`.
 * Thin typed fetch wrappers over /api/v1/metrics-cpc-cpa-epc-avg-sale-cr-roi-roas-revenue-payout-profit.
 * Raw `fetch()` is confined to this file per the dash contract.
 *
 * Shape: report KPI dashboard. Returns 10 performance metrics, each with current
 * value, previous-period value, and sparkline buckets. Optional breakdowns by
 * campaign and affiliate (used by drill-through per metric).
 */

export type MetricKey =
	| "cpc"
	| "cpa"
	| "epc"
	| "avg_sale"
	| "cr"
	| "roi"
	| "roas"
	| "revenue"
	| "payout"
	| "profit";

export interface MetricsSparkBucket {
	t: number;
	value: number;
}

export interface MetricsKpi {
	key: MetricKey;
	label: string;
	value: number;
	previous: number;
	format: "money" | "money_precise" | "number" | "percent" | "ratio";
	spark: MetricsSparkBucket[];
}

export interface MetricsBreakdownRow {
	id: string;
	name: string;
	value: number;
}

export interface MetricsBreakdown {
	metric: MetricKey;
	group: "campaign" | "affiliate";
	rows: MetricsBreakdownRow[];
}

export interface MetricsResponse {
	tenant_id: string;
	period: { from: number; to: number };
	compare_previous: boolean;
	generated_at: number;
	kpis: MetricsKpi[];
	breakdowns?: MetricsBreakdown[];
}

export interface MetricsQueryParams {
	from: number;
	to: number;
	compare?: boolean;
	metric?: MetricKey;
	group?: "campaign" | "affiliate";
}

const BASE = "/api/v1/metrics-cpc-cpa-epc-avg-sale-cr-roi-roas-revenue-payout-profit";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function buildQuery(p: MetricsQueryParams): string {
	const u = new URLSearchParams();
	u.set("from", String(p.from));
	u.set("to", String(p.to));
	if (p.compare) u.set("compare", "1");
	if (p.metric) u.set("metric", p.metric);
	if (p.group) u.set("group", p.group);
	return u.toString();
}

export async function get(params: MetricsQueryParams): Promise<MetricsResponse> {
	const qs = buildQuery(params);
	const res = await fetch(`${BASE}?${qs}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<MetricsResponse>(res);
}

export async function getBreakdown(
	metric: MetricKey,
	params: Omit<MetricsQueryParams, "metric">,
): Promise<MetricsBreakdown> {
	const qs = buildQuery({ ...params, metric, group: params.group ?? "campaign" });
	const res = await fetch(`${BASE}/breakdown?${qs}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<MetricsBreakdown>(res);
}

export const metricsCpcCpaEpcAvgSaleCrRoiRoasRevenuePayoutProfitQueryKeys = {
	all: ["metrics-cpc-cpa-epc-avg-sale-cr-roi-roas-revenue-payout-profit"] as const,
	list: (params: MetricsQueryParams) =>
		[
			...metricsCpcCpaEpcAvgSaleCrRoiRoasRevenuePayoutProfitQueryKeys.all,
			"list",
			params,
		] as const,
	detail: (metric: MetricKey, params: Omit<MetricsQueryParams, "metric">) =>
		[
			...metricsCpcCpaEpcAvgSaleCrRoiRoasRevenuePayoutProfitQueryKeys.all,
			"breakdown",
			metric,
			params,
		] as const,
};
